"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RingDiscoverySession = void 0;
const utils_1 = require("@streamr/utils");
const uuid_1 = require("uuid");
const identifiers_1 = require("../../identifiers");
const ringIdentifiers_1 = require("../contact/ringIdentifiers");
const logger = new utils_1.Logger(module);
class RingDiscoverySession {
    id = (0, uuid_1.v4)();
    noProgressCounter = 0;
    ongoingRequests = new Set();
    doneGate = new utils_1.Gate(false);
    options;
    numContactedPeers = 0;
    targetIdAsRingId;
    constructor(options) {
        this.options = options;
        this.targetIdAsRingId = (0, ringIdentifiers_1.getRingIdFromRaw)(this.options.targetId);
    }
    addContacts(contacts) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        for (const contact of contacts) {
            this.options.peerManager.addContact(contact);
        }
    }
    async fetchClosestContactsFromRemote(contact) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return { left: [], right: [] };
        }
        logger.trace(`Getting closest ring peers from contact: ${(0, identifiers_1.toNodeId)(contact.getPeerDescriptor())}`);
        this.numContactedPeers++;
        this.options.contactedPeers.add(contact.getNodeId());
        const returnedContacts = await contact.getClosestRingPeers(this.options.targetId);
        this.options.peerManager.setContactActive(contact.getNodeId());
        return returnedContacts;
    }
    onRequestSucceeded(nodeId, contacts) {
        if (!this.ongoingRequests.has(nodeId)) {
            return;
        }
        this.ongoingRequests.delete(nodeId);
        const oldClosestContacts = this.options.peerManager.getClosestRingContactsTo(this.options.targetId, 1);
        const oldClosestLeftDistance = (0, ringIdentifiers_1.getLeftDistance)(this.targetIdAsRingId, (0, ringIdentifiers_1.getRingIdFromPeerDescriptor)(oldClosestContacts.left[0].getPeerDescriptor()));
        const oldClosestRightDistance = (0, ringIdentifiers_1.getLeftDistance)(this.targetIdAsRingId, (0, ringIdentifiers_1.getRingIdFromPeerDescriptor)(oldClosestContacts.right[0].getPeerDescriptor()));
        this.addContacts(contacts.left.concat(contacts.right));
        const newClosestContacts = this.options.peerManager.getClosestRingContactsTo(this.options.targetId, 1);
        const newClosestLeftDistance = (0, ringIdentifiers_1.getLeftDistance)(this.targetIdAsRingId, (0, ringIdentifiers_1.getRingIdFromPeerDescriptor)(newClosestContacts.left[0].getPeerDescriptor()));
        const newClosestRightDistance = (0, ringIdentifiers_1.getLeftDistance)(this.targetIdAsRingId, (0, ringIdentifiers_1.getRingIdFromPeerDescriptor)(newClosestContacts.right[0].getPeerDescriptor()));
        if (newClosestLeftDistance >= oldClosestLeftDistance && newClosestRightDistance >= oldClosestRightDistance) {
            this.noProgressCounter++;
        }
    }
    onRequestFailed(peer) {
        if (!this.ongoingRequests.has(peer.getNodeId())) {
            return;
        }
        this.ongoingRequests.delete(peer.getNodeId());
        this.options.peerManager.removeContact(peer.getNodeId());
    }
    findMoreContacts() {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        const uncontacted = this.options.peerManager.getClosestRingContactsTo(this.options.targetId, this.options.parallelism, this.options.contactedPeers);
        if ((uncontacted.left.length === 0 && uncontacted.right.length === 0)
            || this.noProgressCounter >= this.options.noProgressLimit) {
            this.doneGate.open();
            return;
        }
        // ask from both sides equally
        const merged = [];
        const alreadyInMerged = new Set();
        const length = Math.max(uncontacted.left.length, uncontacted.right.length);
        for (let i = 0; i < length; i++) {
            if (i < uncontacted.left.length) {
                if (!alreadyInMerged.has(uncontacted.left[i].getNodeId())) {
                    merged.push(uncontacted.left[i]);
                    alreadyInMerged.add(uncontacted.left[i].getNodeId());
                }
            }
            if (i < uncontacted.right.length) {
                if (!alreadyInMerged.has(uncontacted.right[i].getNodeId())) {
                    merged.push(uncontacted.right[i]);
                    alreadyInMerged.add(uncontacted.right[i].getNodeId());
                }
            }
        }
        for (const nextPeer of merged) {
            if (this.ongoingRequests.size >= this.options.parallelism) {
                break;
            }
            this.ongoingRequests.add(nextPeer.getNodeId());
            // eslint-disable-next-line promise/catch-or-return
            this.fetchClosestContactsFromRemote(nextPeer)
                .then((contacts) => this.onRequestSucceeded(nextPeer.getNodeId(), contacts))
                .catch(() => this.onRequestFailed(nextPeer))
                .finally(() => {
                this.findMoreContacts();
            });
        }
    }
    async findClosestNodes(timeout) {
        if (this.options.peerManager.getNearbyContactCount(this.options.contactedPeers) === 0) {
            return;
        }
        setImmediate(() => {
            this.findMoreContacts();
        });
        await (0, utils_1.withTimeout)(this.doneGate.waitUntilOpen(), timeout, 'discovery session timed out', this.options.abortSignal);
    }
}
exports.RingDiscoverySession = RingDiscoverySession;
//# sourceMappingURL=RingDiscoverySession.js.map